import { TpAssetInfo } from '../assets/interfaces';
import { TpDataContext } from '../interfaces';
import { MutationLedgerPayloadV1 } from '../mutation-ledger/schemas';
import { TpSelectionStoragePayloadV1 } from '../selection/snapshot/selectionFilterSerialization.schemas';
import { TpSnapshotApplicationPlan } from '../selection/snapshot/snapshotApplicationPlan';
import { compressJson } from '../../utils/compression/compressionWorkerClient';
import { buildDashboardStatePayload } from './dashboardStateSerialization';
import { TpDashboardDefinition } from '../../features/visual-analytics/dashboard/interfaces';

/** Validated selection application plan plus the schema-validated ledger awaiting replay. */
export interface DashboardStateApplicationPlan {
  selection: TpSnapshotApplicationPlan;
  selectionSnapshot: TpSelectionStoragePayloadV1;
  mutationLedger: MutationLedgerPayloadV1;
}

let currentDashboardStateClipboardData: { assetType: string; compressed: Uint8Array } | null = null;

/**
 * Serializes the current selection and mutation ledger into one compressed in-memory clipboard
 * entry. The source context is snapshotted before compression, so later mutations cannot alter
 * the payload currently being written.
 */
export async function copyDashboardState(
  dataContext: TpDataContext,
  assetInfo: TpAssetInfo,
  dashboardDef?: TpDashboardDefinition,
): Promise<void> {
  const payload = buildDashboardStatePayload(dataContext, assetInfo, dashboardDef);
  const compressed = await compressJson(payload);
  currentDashboardStateClipboardData = { assetType: assetInfo.type, compressed };
}

/**
 * Returns whether the clipboard contains a dashboard-state payload compatible with the target
 * asset type. This is a cheap metadata check and does not decompress the payload.
 */
export function dashboardStateClipboardHasCompatibleData(assetInfo: TpAssetInfo): boolean {
  return currentDashboardStateClipboardData?.assetType === assetInfo.type;
}

/**
 * Retrieves the compressed dashboard-state payload from the in-memory clipboard
 * if it exists and is compatible with the specified asset type.
 */
export function getCompressedDashboardState(assetInfo: TpAssetInfo): Uint8Array | null {
  if (!currentDashboardStateClipboardData) return null;
  if (!dashboardStateClipboardHasCompatibleData(assetInfo)) return null;
  return currentDashboardStateClipboardData.compressed;
}
